import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Menu, Search, Plus, FileDown, Users, MapPin, Building2, Eye, Pencil, Trash2,
  Upload, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, RefreshCw,
} from "lucide-react";

export default function AdminClients() {
  const { authAxios } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewClient, setViewClient] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());

  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Agents import state
  const [agentsFile, setAgentsFile] = useState(null);
  const [importingAgents, setImportingAgents] = useState(false);
  const [agentsImportResult, setAgentsImportResult] = useState(null);
  // Destinazioni (indirizzi alternativi dei clienti)
  const [destinationsFile, setDestinationsFile] = useState(null);
  const [importingDestinations, setImportingDestinations] = useState(false);
  const [destinationsImportResult, setDestinationsImportResult] = useState(null);

  function getEmptyForm() {
    return {
      ragione_sociale: "", ragione_sociale_2: "", partita_iva: "",
      indirizzo: "", cap: "", citta: "", provincia: "", nazione: "IT",
      cod_pagamento: "", cod_agente: "", telefono: "", email: "", note: "",
    };
  }

  const fetchClients = useCallback(async () => {
    try {
      const res = await authAxios.get("/clients");
      setClients(res.data);
    } catch { toast.error("Errore caricamento clienti"); }
    finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const cities = useMemo(() => [...new Set(clients.map(c => c.citta).filter(Boolean))].sort(), [clients]);
  const agents = useMemo(() => [...new Set(clients.map(c => c.cod_agente).filter(v => v != null))].sort((a, b) => a - b), [clients]);

  const filteredClients = useMemo(() => {
    const list = clients.filter(c => {
      if (cityFilter !== "all" && c.citta !== cityFilter) return false;
      if (agentFilter !== "all" && String(c.cod_agente) !== agentFilter) return false;
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          (c.ragione_sociale || "").toLowerCase().includes(q) ||
          (c.partita_iva || "").toLowerCase().includes(q) ||
          (c.citta || "").toLowerCase().includes(q) ||
          (c.codice_cli || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
    // Ordine alfabetico per ragione sociale (locale italiano, case-insensitive)
    list.sort((a, b) =>
      (a.ragione_sociale || "").localeCompare(b.ragione_sociale || "", "it", { sensitivity: "base" })
    );
    return list;
  }, [clients, searchQuery, cityFilter, agentFilter, statusFilter]);

  const stats = useMemo(() => ({
    total: clients.length,
    confirmed: clients.filter(c => c.status === "confirmed").length,
    standby: clients.filter(c => c.status === "standby").length,
    agents: new Set(clients.map(c => c.cod_agente).filter(Boolean)).size,
    cities: new Set(clients.map(c => c.citta).filter(Boolean)).size,
  }), [clients]);

  // Form handlers
  const openNewForm = () => {
    setEditingClient(null);
    setFormData(getEmptyForm());
    setShowForm(true);
  };

  const openEditForm = (client) => {
    setEditingClient(client);
    setFormData({
      ragione_sociale: client.ragione_sociale || "",
      ragione_sociale_2: client.ragione_sociale_2 || "",
      partita_iva: client.partita_iva || "",
      indirizzo: client.indirizzo || "",
      cap: client.cap || "",
      citta: client.citta || "",
      provincia: client.provincia || "",
      nazione: client.nazione || "IT",
      cod_pagamento: client.cod_pagamento || "",
      cod_agente: client.cod_agente ?? "",
      telefono: client.telefono || "",
      email: client.email || "",
      note: client.note || "",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.ragione_sociale.trim()) { toast.error("Ragione sociale obbligatoria"); return; }
    setSaving(true);
    try {
      const payload = { ...formData, cod_agente: formData.cod_agente === "" ? null : parseInt(formData.cod_agente) };
      if (editingClient) {
        await authAxios.put(`/clients/${editingClient.id}`, payload);
        toast.success("Cliente aggiornato");
      } else {
        await authAxios.post("/clients", payload);
        toast.success("Cliente creato");
      }
      setShowForm(false);
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await authAxios.delete(`/clients/${deleteConfirm.id}`);
      toast.success("Cliente eliminato");
      setDeleteConfirm(null);
      fetchClients();
    } catch { toast.error("Errore eliminazione"); }
  };

  const downloadPdf = async (clientId) => {
    try {
      const res = await authAxios.post(`/clients/${clientId}/pdf`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "scheda_cliente.pdf"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore generazione PDF"); }
  };

  // Import handlers
  const validateFile = (f) => {
    const ext = f.name.toLowerCase().substring(f.name.lastIndexOf('.'));
    if (!['.xls', '.xlsx', '.csv'].includes(ext)) {
      toast.error("Formato non supportato. Usa .xls, .xlsx o .csv");
      return false;
    }
    return true;
  };

  const handleImportClients = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const res = await authAxios.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(res.data);
      toast.success(`Importati ${res.data.imported} clienti, aggiornati ${res.data.updated}`);
      setImportFile(null);
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore importazione"); }
    finally { setImporting(false); }
  };

  const handleImportAgents = async () => {
    if (!agentsFile) return;
    setImportingAgents(true);
    setAgentsImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", agentsFile);
      const res = await authAxios.post("/agents/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAgentsImportResult(res.data);
      toast.success(res.data.message);
      setAgentsFile(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore importazione agenti"); }
    finally { setImportingAgents(false); }
  };

  const handleImportDestinations = async () => {
    if (!destinationsFile) return;
    setImportingDestinations(true);
    setDestinationsImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", destinationsFile);
      const res = await authAxios.post("/destinations/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDestinationsImportResult(res.data);
      toast.success(`Importate ${res.data.imported} destinazioni (saltate ${res.data.skipped})`);
      setDestinationsFile(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore importazione destinazioni"); }
    finally { setImportingDestinations(false); }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="admin-clients-page">
      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Clienti" />
        <div className="mb-6 flex items-center gap-4">
          <div className="flex-1">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Gestione Clienti</h1>
            <p className="text-zinc-400 text-sm">Anagrafica clienti, importazione e gestione</p>
          </div>
        </div>

        <Tabs defaultValue="list" className="space-y-6">
          <TabsList className="bg-[#18181b] border border-white/5 w-full overflow-x-auto justify-start">
            <TabsTrigger value="list" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black shrink-0">
              <Users className="w-4 h-4 mr-2" /> Anagrafica
            </TabsTrigger>
            <TabsTrigger value="import" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-black shrink-0">
              <Upload className="w-4 h-4 mr-2" /> Importa Clienti
            </TabsTrigger>
            <TabsTrigger value="agents" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white shrink-0">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Importa Agenti
            </TabsTrigger>
            <TabsTrigger value="destinations" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black shrink-0">
              <MapPin className="w-4 h-4 mr-2" /> Importa Destinazioni
            </TabsTrigger>
          </TabsList>

          {/* TAB: Anagrafica Clienti */}
          <TabsContent value="list" className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { label: "Totale", value: stats.total, color: "text-blue-400", bg: "bg-blue-500/10" },
                { label: "Confermati", value: stats.confirmed, color: "text-emerald-400", bg: "bg-emerald-500/10" },
                { label: "Standby", value: stats.standby, color: "text-orange-400", bg: "bg-orange-500/10" },
                { label: "Agenti", value: stats.agents, color: "text-purple-400", bg: "bg-purple-500/10" },
                { label: "Citta", value: stats.cities, color: "text-cyan-400", bg: "bg-cyan-500/10" },
              ].map(s => (
                <Card key={s.label} className="bg-[#18181b] border-white/5">
                  <CardContent className="p-3 text-center">
                    <p className={`text-2xl font-bold font-mono ${s.color}`}>{s.value}</p>
                    <p className="text-[11px] text-zinc-500">{s.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input placeholder="Cerca per nome, P.IVA, citta..." value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#18181b] border-white/10" data-testid="admin-client-search" />
              </div>
              <Select value={cityFilter} onValueChange={setCityFilter}>
                <SelectTrigger className="w-[160px] bg-[#18181b] border-white/10">
                  <SelectValue placeholder="Citta" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-white/10 max-h-60">
                  <SelectItem value="all">Tutte le citta</SelectItem>
                  {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={agentFilter} onValueChange={setAgentFilter}>
                <SelectTrigger className="w-[140px] bg-[#18181b] border-white/10">
                  <SelectValue placeholder="Agente" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-white/10">
                  <SelectItem value="all">Tutti gli agenti</SelectItem>
                  {agents.map(a => <SelectItem key={a} value={String(a)}>Agente {a}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] bg-[#18181b] border-white/10">
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-white/10">
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="confirmed">Confermati</SelectItem>
                  <SelectItem value="standby">Standby</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline" className="text-zinc-400 border-white/10">{filteredClients.length} risultati</Badge>
              <Button onClick={openNewForm} className="bg-amber-500 hover:bg-amber-600 text-black ml-auto" data-testid="admin-new-client-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuovo Cliente
              </Button>
            </div>

            {/* Clients Table */}
            <Card className="bg-[#18181b] border-white/5">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
                  </div>
                ) : filteredClients.length === 0 ? (
                  <div className="py-12 text-center text-zinc-500">
                    {clients.length === 0 ? "Nessun cliente. Importa un file o creane uno nuovo." : "Nessun risultato"}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/5 hover:bg-transparent">
                          <TableHead className="text-zinc-400">Ragione Sociale</TableHead>
                          <TableHead className="text-zinc-400 hidden sm:table-cell">P.IVA</TableHead>
                          <TableHead className="text-zinc-400 hidden md:table-cell">Citta</TableHead>
                          <TableHead className="text-zinc-400 hidden lg:table-cell">Agente</TableHead>
                          <TableHead className="text-zinc-400 text-center">Stato</TableHead>
                          <TableHead className="text-zinc-400 text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredClients.slice(0, 200).map(c => (
                          <TableRow key={c.id} className="border-white/5">
                            <TableCell>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="font-medium text-white text-sm">{c.ragione_sociale}</p>
                                {c.tipo_listino === 2 && (
                                  <span
                                    data-testid={`client-listino2-badge-${c.id}`}
                                    title="Listino 2 (dedicato) — gestito dal programma centrale"
                                    className="text-[9px] font-bold font-mono bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 rounded px-1.5 py-[1px]"
                                  >
                                    L2
                                  </span>
                                )}
                                {c.sconti_fissi && (
                                  <span
                                    data-testid={`client-sconti-fissi-badge-${c.id}`}
                                    title="Cliente contrattualizzato — sconti non modificabili, nascosti nel PDF Preparazione"
                                    className="text-[9px] font-bold font-mono bg-red-500/15 border border-red-500/40 text-red-300 rounded px-1.5 py-[1px]"
                                  >
                                    CONTRATTUALIZZATO
                                  </span>
                                )}
                              </div>
                              {c.ragione_sociale_2 && <p className="text-xs text-zinc-500">{c.ragione_sociale_2}</p>}
                            </TableCell>
                            <TableCell className="font-mono text-zinc-400 text-sm hidden sm:table-cell">{c.partita_iva || "-"}</TableCell>
                            <TableCell className="text-zinc-400 text-sm hidden md:table-cell">
                              {c.citta || "-"}{c.provincia ? ` (${c.provincia})` : ""}
                            </TableCell>
                            <TableCell className="text-zinc-400 text-sm hidden lg:table-cell font-mono">{c.cod_agente ?? "-"}</TableCell>
                            <TableCell className="text-center">
                              {c.status === "standby" ? (
                                <Badge className="bg-orange-500/20 text-orange-400 text-[10px]">Standby</Badge>
                              ) : (
                                <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Confermato</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-0.5">
                                <Button size="icon" variant="ghost" onClick={() => setViewClient(c)} className="h-7 w-7 text-zinc-400 hover:text-blue-400">
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => openEditForm(c)} className="h-7 w-7 text-zinc-400 hover:text-amber-400">
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => downloadPdf(c.id)} className="h-7 w-7 text-zinc-400 hover:text-emerald-400 hidden sm:inline-flex">
                                  <FileDown className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" onClick={() => setDeleteConfirm(c)} className="h-7 w-7 text-zinc-400 hover:text-red-400">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {filteredClients.length > 200 && (
                      <div className="p-3 text-center text-zinc-500 text-sm">
                        Mostrando 200 di {filteredClients.length}. Usa i filtri per affinare.
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Importa Clienti */}
          <TabsContent value="import" className="space-y-6">
            <Card className="bg-emerald-500/10 border-emerald-500/20">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-400 mt-0.5" />
                  <div>
                    <p className="text-emerald-200 text-sm"><strong>Importa Anagrafica Clienti</strong> da file XLS/XLSX/CSV</p>
                    <p className="text-emerald-200/70 text-xs mt-1">
                      Colonne riconosciute automaticamente: RagSoc1, PartIva, Indirizzo, Cap, Citta, Prov, Nazione, CodPagamento, Agente, IdCliente, CodiceCli, Telefono, Email, Note
                    </p>
                    <p className="text-emerald-200/70 text-xs mt-0.5">
                      I clienti esistenti vengono aggiornati (match per P.IVA o IdCliente). I clienti standby vengono sostituiti se esiste un confermato con la stessa P.IVA.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#18181b] border-white/5">
              <CardContent className="p-6">
                <div
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                    dragActive ? "border-emerald-500 bg-emerald-500/5" : "border-white/10 hover:border-white/20"
                  }`}
                  onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={(e) => {
                    e.preventDefault(); setDragActive(false);
                    if (e.dataTransfer.files?.[0] && validateFile(e.dataTransfer.files[0])) {
                      setImportFile(e.dataTransfer.files[0]); setImportResult(null);
                    }
                  }}
                >
                  <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
                  <p className="text-zinc-300 mb-2">Trascina il file qui oppure</p>
                  <label className="cursor-pointer">
                    <Button variant="outline" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" asChild>
                      <span>Seleziona File</span>
                    </Button>
                    <input type="file" accept=".xls,.xlsx,.csv" className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0] && validateFile(e.target.files[0])) {
                          setImportFile(e.target.files[0]); setImportResult(null);
                        }
                      }} />
                  </label>
                  <p className="text-zinc-500 text-xs mt-2">Formati: .xls, .xlsx, .csv</p>
                </div>

                {importFile && (
                  <div className="mt-4 flex items-center justify-between bg-[#27272a] rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
                      <div>
                        <p className="text-white text-sm font-medium">{importFile.name}</p>
                        <p className="text-zinc-500 text-xs">{(importFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => { setImportFile(null); setImportResult(null); }} className="text-zinc-400">
                        Rimuovi
                      </Button>
                      <Button size="sm" onClick={handleImportClients} disabled={importing}
                        className="bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="import-clients-btn">
                        {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        {importing ? "Importando..." : "Importa"}
                      </Button>
                    </div>
                  </div>
                )}

                {importResult && (
                  <Card className="mt-4 bg-[#27272a] border-white/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400" /> Importazione Completata
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-2 bg-emerald-500/10 rounded-lg">
                          <p className="text-xl font-bold text-emerald-400 font-mono">{importResult.imported}</p>
                          <p className="text-xs text-zinc-400">Importati</p>
                        </div>
                        <div className="text-center p-2 bg-blue-500/10 rounded-lg">
                          <p className="text-xl font-bold text-blue-400 font-mono">{importResult.updated}</p>
                          <p className="text-xs text-zinc-400">Aggiornati</p>
                        </div>
                        <div className="text-center p-2 bg-zinc-500/10 rounded-lg">
                          <p className="text-xl font-bold text-zinc-400 font-mono">{importResult.total_rows}</p>
                          <p className="text-xs text-zinc-400">Righe Totali</p>
                        </div>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-red-400 text-sm flex items-center gap-1 mb-1"><AlertTriangle className="w-4 h-4" /> Errori:</p>
                          {importResult.errors.map((e) => <p key={`err-${String(e).slice(0, 96)}`} className="text-red-300/70 text-xs">{e}</p>)}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Importa Agenti */}
          <TabsContent value="agents" className="space-y-6">
            <Card className="bg-purple-500/10 border-purple-500/20">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-purple-400 mt-0.5" />
                  <div>
                    <p className="text-purple-200 text-sm"><strong>Importa Anagrafica Agenti</strong> da file XLS/XLSX/CSV</p>
                    <p className="text-purple-200/70 text-xs mt-1">
                      Il file deve contenere 2 colonne: cod_anag (codice anagrafica) e cod_agente (codice agente)
                    </p>
                    <p className="text-purple-200/70 text-xs mt-0.5">
                      Questa tabella viene usata per mappare le anagrafiche ai rispettivi agenti.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#18181b] border-white/5">
              <CardContent className="p-6">
                <div className="border-2 border-dashed rounded-xl p-8 text-center border-white/10 hover:border-white/20 transition-colors">
                  <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
                  <p className="text-zinc-300 mb-2">Trascina il file qui oppure</p>
                  <label className="cursor-pointer">
                    <Button variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10" asChild>
                      <span>Seleziona File</span>
                    </Button>
                    <input type="file" accept=".xls,.xlsx,.csv" className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0] && validateFile(e.target.files[0])) {
                          setAgentsFile(e.target.files[0]); setAgentsImportResult(null);
                        }
                      }} />
                  </label>
                  <p className="text-zinc-500 text-xs mt-2">Formati: .xls, .xlsx, .csv</p>
                </div>

                {agentsFile && (
                  <div className="mt-4 flex items-center justify-between bg-[#27272a] rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="w-5 h-5 text-purple-400" />
                      <div>
                        <p className="text-white text-sm font-medium">{agentsFile.name}</p>
                        <p className="text-zinc-500 text-xs">{(agentsFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => { setAgentsFile(null); setAgentsImportResult(null); }} className="text-zinc-400">
                        Rimuovi
                      </Button>
                      <Button size="sm" onClick={handleImportAgents} disabled={importingAgents}
                        className="bg-purple-500 hover:bg-purple-600 text-white" data-testid="import-agents-btn">
                        {importingAgents ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        {importingAgents ? "Importando..." : "Importa"}
                      </Button>
                    </div>
                  </div>
                )}

                {agentsImportResult && (
                  <Card className="mt-4 bg-[#27272a] border-white/5">
                    <CardContent className="p-4 flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-purple-400" />
                      <p className="text-white text-sm">{agentsImportResult.message}</p>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB: Importa Destinazioni */}
          <TabsContent value="destinations" className="space-y-6">
            <Card className="bg-cyan-500/10 border-cyan-500/20">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-cyan-400 mt-0.5" />
                  <div>
                    <p className="text-cyan-200 text-sm"><strong>Importa Destinazioni</strong> da file XLS/XLSX/CSV</p>
                    <p className="text-cyan-200/70 text-xs mt-1">
                      Le destinazioni sono indirizzi alternativi dei clienti (filiali, negozi, magazzini).
                      Vengono associate al cliente tramite <code className="bg-black/30 px-1 rounded">codice_cli</code>.
                    </p>
                    <p className="text-cyan-200/70 text-xs mt-1">
                      Colonne attese: <code className="bg-black/30 px-1 rounded">codice_cli</code>,
                      <code className="bg-black/30 px-1 rounded ml-1">prog_destin/codice_dest</code>,
                      <code className="bg-black/30 px-1 rounded ml-1">denominaz</code>,
                      <code className="bg-black/30 px-1 rounded ml-1">indirizzo</code>,
                      <code className="bg-black/30 px-1 rounded ml-1">localita</code>,
                      <code className="bg-black/30 px-1 rounded ml-1">cap</code>,
                      <code className="bg-black/30 px-1 rounded ml-1">provincia</code>,
                      <code className="bg-black/30 px-1 rounded ml-1">nazione</code>,
                      <code className="bg-black/30 px-1 rounded ml-1">telefono</code>.
                    </p>
                    <p className="text-cyan-200/70 text-xs mt-1">
                      File senza header (es. dal gestionale): le prime 9 colonne vengono lette in ordine posizionale.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#18181b] border-white/5">
              <CardContent className="p-6">
                <div className="border-2 border-dashed rounded-xl p-8 text-center border-white/10 hover:border-white/20 transition-colors">
                  <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
                  <p className="text-zinc-300 mb-2">Trascina il file qui oppure</p>
                  <label className="cursor-pointer">
                    <Button variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" asChild>
                      <span>Seleziona File</span>
                    </Button>
                    <input type="file" accept=".xls,.xlsx,.csv" className="hidden"
                      data-testid="destinations-file-input"
                      onChange={(e) => {
                        if (e.target.files?.[0] && validateFile(e.target.files[0])) {
                          setDestinationsFile(e.target.files[0]); setDestinationsImportResult(null);
                        }
                      }} />
                  </label>
                  <p className="text-zinc-500 text-xs mt-2">Formati: .xls, .xlsx, .csv</p>
                </div>

                {destinationsFile && (
                  <div className="mt-4 flex items-center justify-between bg-[#27272a] rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-cyan-400" />
                      <div>
                        <p className="text-white text-sm font-medium">{destinationsFile.name}</p>
                        <p className="text-zinc-500 text-xs">{(destinationsFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => { setDestinationsFile(null); setDestinationsImportResult(null); }} className="text-zinc-400">
                        Rimuovi
                      </Button>
                      <Button size="sm" onClick={handleImportDestinations} disabled={importingDestinations}
                        className="bg-cyan-500 hover:bg-cyan-600 text-black" data-testid="import-destinations-btn">
                        {importingDestinations ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        {importingDestinations ? "Importando..." : "Importa"}
                      </Button>
                    </div>
                  </div>
                )}

                {destinationsImportResult && (
                  <Card className="mt-4 bg-[#27272a] border-white/5">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <CheckCircle className="w-5 h-5 text-cyan-400" />
                        <p className="text-white text-sm font-medium">
                          Importate {destinationsImportResult.imported} · Saltate {destinationsImportResult.skipped}
                        </p>
                      </div>
                      {destinationsImportResult.errors?.length > 0 && (
                        <div className="mt-2 max-h-40 overflow-y-auto text-xs text-amber-300 space-y-1">
                          {destinationsImportResult.errors.slice(0, 10).map((e) => (
                            <p key={`dest-err-${String(e).slice(0, 96)}`}>• {e}</p>
                          ))}
                          {destinationsImportResult.errors.length > 10 && (
                            <p className="italic">... e altri {destinationsImportResult.errors.length - 10} errori</p>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* View Client Dialog */}
        <Dialog open={!!viewClient} onOpenChange={() => setViewClient(null)}>
          <DialogContent className="bg-[#18181b] border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-400" /> {viewClient?.ragione_sociale}
              </DialogTitle>
              <DialogDescription className="sr-only">Dettaglio cliente</DialogDescription>
              <div className="mt-1">
                {viewClient?.status === "standby" ? (
                  <Badge className="bg-orange-500/20 text-orange-400">Proposta - Standby</Badge>
                ) : (
                  <Badge className="bg-emerald-500/20 text-emerald-400">Confermato</Badge>
                )}
              </div>
            </DialogHeader>
            {viewClient && (
              <div className="space-y-3 text-sm">
                {[
                  ["Ragione Sociale 2", viewClient.ragione_sociale_2],
                  ["Codice Cliente", viewClient.codice_cli],
                  ["Partita IVA", viewClient.partita_iva],
                  ["Indirizzo", viewClient.indirizzo],
                  ["CAP", viewClient.cap],
                  ["Citta", viewClient.citta],
                  ["Provincia", viewClient.provincia],
                  ["Nazione", viewClient.nazione],
                  ["Cod. Pagamento", viewClient.cod_pagamento],
                  ["Cod. Agente", viewClient.cod_agente],
                  ["Tipo Listino", viewClient.tipo_listino === 2 ? "Listino 2 (dedicato)" : "Listino 1 (base)"],
                  ["Sconto Canale", viewClient.sconto_canale > 0 ? `${viewClient.sconto_canale}%` : null],
                  ["Telefono", viewClient.telefono],
                  ["Email", viewClient.email],
                  ["Note", viewClient.note],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} className="flex justify-between border-b border-white/5 pb-2">
                    <span className="text-zinc-400">{label}</span>
                    <span className="text-white font-medium text-right max-w-[60%]">{value}</span>
                  </div>
                ))}

                {/* Estratto conto toggle */}
                <div className="flex items-start justify-between border-b border-white/5 pb-2 gap-3">
                  <div className="min-w-0">
                    <p className="text-zinc-400">Estratto conto nei Giri</p>
                    <p className="text-zinc-600 text-[11px]">Se escluso, l'agente non vede il banner "fatture da pagare"</p>
                  </div>
                  <button
                    onClick={async () => {
                      const newVal = !viewClient.escluso_estratto_conto;
                      try {
                        await authAxios.put(`/clients/${viewClient.id}/toggle-estratto-conto`, { escluso: newVal });
                        setViewClient({ ...viewClient, escluso_estratto_conto: newVal });
                        setClients(prev => prev.map(c => c.id === viewClient.id ? { ...c, escluso_estratto_conto: newVal } : c));
                        toast.success(newVal ? "Escluso dall'estratto conto" : "Re-incluso nell'estratto conto");
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Errore");
                      }
                    }}
                    data-testid="toggle-escluso-ec"
                    className={`px-3 py-1 rounded-md text-xs font-semibold border transition-colors shrink-0 ${
                      viewClient.escluso_estratto_conto
                        ? "border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                        : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                    }`}
                  >
                    {viewClient.escluso_estratto_conto ? "Escluso" : "Attivo"}
                  </button>
                </div>

                {/* Contrattualizzato toggle */}
                <div className="flex items-start justify-between border-b border-white/5 pb-2 gap-3">
                  <div className="min-w-0">
                    <p className="text-zinc-400">Cliente Contrattualizzato</p>
                    <p className="text-zinc-600 text-[11px]">Se attivo, prezzi e sconti in ordine/preventivo sono bloccati (non modificabili) e nascosti dal PDF Preparazione</p>
                  </div>
                  <button
                    onClick={async () => {
                      const newVal = !viewClient.sconti_fissi;
                      try {
                        await authAxios.put(`/clients/${viewClient.id}/sconti-fissi`, { sconti_fissi: newVal });
                        setViewClient({ ...viewClient, sconti_fissi: newVal });
                        setClients(prev => prev.map(c => c.id === viewClient.id ? { ...c, sconti_fissi: newVal } : c));
                        toast.success(newVal ? "Contrattualizzato attivato" : "Contrattualizzato disattivato");
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Errore");
                      }
                    }}
                    data-testid="toggle-sconti-fissi"
                    className={`px-3 py-1 rounded-md text-xs font-semibold border transition-colors shrink-0 ${
                      viewClient.sconti_fissi
                        ? "border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                        : "border-white/10 bg-white/5 text-zinc-400 hover:bg-white/10"
                    }`}
                  >
                    {viewClient.sconti_fissi ? "Contrattualizzato" : "Standard"}
                  </button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => downloadPdf(viewClient?.id)} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                <FileDown className="w-4 h-4 mr-2" /> PDF
              </Button>
              <Button variant="outline" onClick={() => setViewClient(null)} className="border-white/10">Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Client Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="bg-[#18181b] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">{editingClient ? "Modifica Cliente" : "Nuovo Cliente"}</DialogTitle>
              <DialogDescription className="text-zinc-400">
                {editingClient ? "Modifica i dati del cliente" : "Crea un nuovo cliente confermato"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-zinc-300 text-xs">Ragione Sociale *</Label>
                <Input value={formData.ragione_sociale} onChange={e => setFormData(p => ({ ...p, ragione_sociale: e.target.value }))}
                  className="bg-[#27272a] border-white/10" data-testid="admin-form-ragione-sociale" />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Ragione Sociale 2</Label>
                <Input value={formData.ragione_sociale_2} onChange={e => setFormData(p => ({ ...p, ragione_sociale_2: e.target.value }))}
                  className="bg-[#27272a] border-white/10" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs">Partita IVA</Label>
                  <Input value={formData.partita_iva} onChange={e => setFormData(p => ({ ...p, partita_iva: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Cod. Agente</Label>
                  <Input type="number" value={formData.cod_agente} onChange={e => setFormData(p => ({ ...p, cod_agente: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Indirizzo</Label>
                <Input value={formData.indirizzo} onChange={e => setFormData(p => ({ ...p, indirizzo: e.target.value }))}
                  className="bg-[#27272a] border-white/10" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs">CAP</Label>
                  <Input value={formData.cap} onChange={e => setFormData(p => ({ ...p, cap: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Citta</Label>
                  <Input value={formData.citta} onChange={e => setFormData(p => ({ ...p, citta: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Prov.</Label>
                  <Input value={formData.provincia} onChange={e => setFormData(p => ({ ...p, provincia: e.target.value }))}
                    className="bg-[#27272a] border-white/10" maxLength={2} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs">Telefono</Label>
                  <Input value={formData.telefono} onChange={e => setFormData(p => ({ ...p, telefono: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Email</Label>
                  <Input value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-xs">Cod. Pagamento</Label>
                  <Input value={formData.cod_pagamento} onChange={e => setFormData(p => ({ ...p, cod_pagamento: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-xs">Nazione</Label>
                  <Input value={formData.nazione} onChange={e => setFormData(p => ({ ...p, nazione: e.target.value }))}
                    className="bg-[#27272a] border-white/10" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Note</Label>
                <Input value={formData.note} onChange={e => setFormData(p => ({ ...p, note: e.target.value }))}
                  className="bg-[#27272a] border-white/10" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-white/10">Annulla</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="admin-save-client-btn">
                {saving ? "Salvataggio..." : editingClient ? "Salva" : "Crea Cliente"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirm Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="bg-[#18181b] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Conferma Eliminazione</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Vuoi eliminare il cliente <strong className="text-white">{deleteConfirm?.ragione_sociale}</strong>? Questa azione non puo essere annullata.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="border-white/10">Annulla</Button>
              <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Elimina</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
